// Copyright 2026 FlairCV Inc.
// SPDX-License-Identifier: LicenseRef-FlairCV-Enterprise

import { Link, useMatchRoute } from '@tanstack/react-router';
import {
  Briefcase,
  CreditCard,
  FileText,
  GraduationCap,
  Layers,
  LayoutDashboard,
  Palette,
  Sparkles,
  Target,
  Upload,
  User,
  Wrench,
} from 'lucide-react';
import { useCreditBalance } from '@/app/features/credit';
import { useCreditStore } from '@/app/stores/credit-store';
import { cn } from '@/lib/utils';

interface NavItem {
  label: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  rightAccent?: 'blue' | 'green';
}

interface SidebarNavProps {
  collapsed?: boolean;
}

const primaryNavItems: NavItem[] = [
  { label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { label: 'Profile', href: '/profile', icon: User },
  { label: 'Experience', href: '/experience', icon: Briefcase },
  { label: 'Accomplishments', href: '/accomplishments', icon: Target },
  { label: 'Skills', href: '/skills', icon: Sparkles },
  { label: 'Education', href: '/education', icon: GraduationCap },
  { label: 'Custom Sections', href: '/custom-sections', icon: Layers },
];

const priorityNavItems: NavItem[] = [
  { label: 'Applications', href: '/applications', icon: FileText, rightAccent: 'green' },
  { label: 'Import CV', href: '/import/cv-upload', icon: Upload, rightAccent: 'blue' },
];

const bottomNavItems: NavItem[] = [
  { label: 'Credits', href: '/credits', icon: CreditCard },
  { label: 'Prompts', href: '/prompts', icon: Wrench },
  { label: 'CV Templates', href: '/settings/templates', icon: Palette },
];

export function SidebarNav({ collapsed }: SidebarNavProps) {
  const matchRoute = useMatchRoute();
  const { balance } = useCreditStore();
  useCreditBalance(); // Fetch + sync balance; keeps sidebar fresh after AI actions.

  const formattedBalance = `$${parseFloat(balance).toFixed(2)}`;

  function getPriorityBorderClass(accent?: NavItem['rightAccent']) {
    if (accent === 'green') return 'border-emerald-500/80';
    if (accent === 'blue') return 'border-blue-500/80';
    return 'border-transparent';
  }

  function renderNavItem(item: NavItem, usePriorityBorder?: boolean) {
    const isActive = matchRoute({ to: item.href, fuzzy: true });
    const isCredits = item.label === 'Credits';
    return (
      <Link
        key={item.href}
        to={item.href}
        title={collapsed ? (isCredits ? `Credits ${formattedBalance}` : item.label) : undefined}
        className={cn(
          'group relative flex items-center rounded-lg transition-all duration-150',
          collapsed
            ? 'justify-center p-2'
            : 'gap-3 border-l-2 px-3 py-2 lg:py-2.5 text-[14px] lg:text-[15px] font-medium',
          isActive
            ? collapsed
              ? 'bg-sidebar-accent text-sidebar-accent-foreground'
              : 'border-primary bg-sidebar-accent text-sidebar-accent-foreground'
            : collapsed
              ? 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
              : cn(
                  usePriorityBorder ? getPriorityBorderClass(item.rightAccent) : 'border-transparent',
                  'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                ),
        )}
        aria-current={isActive ? 'page' : undefined}
      >
        <item.icon
          className={cn(
            'shrink-0',
            collapsed ? 'h-[18px] w-[18px]' : 'h-4 w-4 lg:h-[18px] lg:w-[18px]',
            isActive ? 'text-primary' : 'text-sidebar-foreground/80',
          )}
        />
        {!collapsed && <span className={isCredits ? 'flex-1' : undefined}>{item.label}</span>}
        {!collapsed && isCredits && (
          <span className="font-mono text-[13px] font-semibold tabular-nums text-sidebar-foreground/60">
            {formattedBalance}
          </span>
        )}
        {/* Tooltip on hover when collapsed */}
        {collapsed && (
          <span className="pointer-events-none absolute left-full ml-2 z-50 hidden whitespace-nowrap rounded-md bg-foreground px-2 py-1 text-[12px] font-medium text-background shadow-md group-hover:block">
            {isCredits ? `Credits ${formattedBalance}` : item.label}
          </span>
        )}
      </Link>
    );
  }

  return (
    <nav
      className={cn(
        'flex flex-1 flex-col gap-0.5 lg:gap-1',
        collapsed ? 'items-center px-1.5' : 'px-2 lg:px-3',
      )}
      aria-label="Main navigation"
    >
      <div className={cn('flex flex-col gap-0.5 lg:gap-1', collapsed && 'w-full')}>
        {primaryNavItems.map((item) => renderNavItem(item))}

        <div className={cn('my-2 h-px bg-sidebar-border/50', collapsed ? 'mx-1' : 'mx-3')} role="separator" />

        {priorityNavItems.map((item) => renderNavItem(item, true))}
      </div>

      <div className={cn('h-px bg-sidebar-border/50', collapsed ? 'my-2 mx-1' : 'my-3 lg:my-4 mx-3')} role="separator" />

      <div className={cn('flex flex-col gap-0.5 lg:gap-1', collapsed && 'w-full')}>
        {bottomNavItems.map((item) => renderNavItem(item))}
      </div>
    </nav>
  );
}
